import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE_URL } from '../config';
import {
  announceAcademicYearChange,
  getEbsToken,
  getSelectedYear,
  setSelectedYear,
  startAcademicYearMonitor,
} from '../config/academicYear';
import './AcademicYearSelector.css';

export default function AcademicYearSelector() {
  const [years, setYears] = useState([]);
  const [selected, setSelected] = useState(getSelectedYear());
  const [headerHost, setHeaderHost] = useState(null);
  const [open, setOpen] = useState(false);
  const selectorRef = useRef(null);

  useEffect(() => {
    const root = document.getElementById('root') || document.body;
    const locateHeader = () => {
      const nextHost = document.querySelector('.header .header-right') || document.querySelector('.header .header-content');
      setHeaderHost((current) => current === nextHost ? current : nextHost);
    };
    locateHeader();
    const observer = new MutationObserver(locateHeader);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    return startAcademicYearMonitor(
      async () => {
        const token = getEbsToken();
        const response = await fetch(`${API_BASE_URL}/api/academic-years`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('Akademik yillar yuklenemedi');
        return response.json();
      },
      (result) => {
        const refreshForNewCurrentYear = announceAcademicYearChange(result);
        setYears(result.years);
        setSelected(result.selected);
        if (result.urlNeedsUpdate) {
          const currentPath = window.location.pathname || '/';
          const nextPath = /^\/\d{4}(?=\/|$)/.test(currentPath)
            ? currentPath.replace(/^\/\d{4}(?=\/|$)/, `/${result.selected}`)
            : `/${result.selected}${currentPath === '/' ? '/' : currentPath}`;
          window.location.replace(`${nextPath}${window.location.search}${window.location.hash}`);
          return;
        }
        if (refreshForNewCurrentYear) {
          window.location.reload();
        }
      }
    );
  }, []);

  if (years.length === 0 || !headerHost) return null;
  const meta = years.find((item) => item.year === selected);
  const planning = meta?.status === 'planning';
  const readOnly = Boolean(meta && !meta.editable && !planning);
  const selectedLabel = meta?.label || selected;

  return createPortal(
    <div
      className="academic-year-selector ebs-academic-year"
      ref={selectorRef}
      title={planning ? 'Hazırlık akademik yılı' : readOnly ? 'Arşiv akademik yılı — salt okunur' : 'Aktif akademik yıl'}
    >
      <button
        type="button"
        className="ay-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Akademik yıl seçimi"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="ay-value">{selectedLabel}</span>
        <span className={`ay-chevron${open ? ' is-open' : ''}`} aria-hidden="true">
          <svg viewBox="0 0 12 12" fill="none"><path d="M2.5 4.5 6 8l3.5-3.5" /></svg>
        </span>
      </button>
      {open && (
        <div className="ay-menu" role="listbox" aria-label="Akademik yıllar">
          {years.map((year) => (
            <button
              key={year.year}
              type="button"
              role="option"
              aria-selected={year.year === selected}
              className={`ay-option${year.year === selected ? ' is-selected' : ''}`}
              onClick={() => {
                setOpen(false);
                if (year.year !== selected) setSelectedYear(year.year);
              }}
            >
              {year.label}
            </button>
          ))}
        </div>
      )}
    </div>,
    headerHost
  );
}
